// Wall-clock → UTC conversion in a specific IANA timezone. A coach in New York
// scheduling "12:00" for a California restaurant means noon PACIFIC — the
// device timezone must not leak into the timestamp we submit.

import tzLookup from '@photostructure/tz-lookup';

const pad = (value) => String(value).padStart(2, '0');

const parseWallClock = (dateValue, timeValue) => {
  const dateMatch = String(dateValue ?? '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!dateMatch) return null;
  const timeMatch = String(timeValue ?? '00:00').trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!timeMatch) return null;
  return {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
    second: Number(timeMatch[3] || 0),
  };
};

// Offset (ms) of `timeZone` from UTC at the given UTC instant.
const timezoneOffsetMs = (utcMs, timeZone) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(utcMs))
      .map((part) => [part.type, part.value]),
  );
  const zonedAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return zonedAsUtc - utcMs;
};

/**
 * Convert a wall-clock date ("YYYY-MM-DD") + time ("HH:MM[:SS]") in the given
 * IANA timezone to a UTC ISO string. Without a usable timezone this falls back
 * to the device-local interpretation (the previous behavior), so callers can
 * pass a possibly-missing restaurant timezone safely. Returns null when the
 * date/time is unparseable.
 */
export function wallClockToIso(dateValue, timeValue, timeZone) {
  const wall = parseWallClock(dateValue, timeValue);
  if (!wall) return null;

  const localFallback = () => {
    const parsed = new Date(
      `${wall.year}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(wall.minute)}:${pad(wall.second)}`,
    );
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  };

  const zone = String(timeZone ?? '').trim();
  if (!zone) return localFallback();

  try {
    const wallAsUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
    // Two passes converge across DST transitions: the first guess uses the
    // offset at the wall time read as UTC, the second re-reads at the result.
    let utcMs = wallAsUtc - timezoneOffsetMs(wallAsUtc, zone);
    utcMs = wallAsUtc - timezoneOffsetMs(utcMs, zone);
    return new Date(utcMs).toISOString();
  } catch {
    // Unknown/invalid timezone identifier — behave like before rather than fail.
    return localFallback();
  }
}

/**
 * Format an instant (Date, ms, or ISO string) as wall-clock date/time input
 * values ("YYYY-MM-DD" / "HH:mm") in the given IANA timezone. Without a usable
 * timezone it falls back to the device-local wall clock. Returns null when the
 * instant is unparseable.
 */
// One formatter per zone: building an Intl.DateTimeFormat is the slow part,
// and the calendar asks for every event's day on every sort. A zone the
// platform does not know is remembered as such (null) instead of throwing on
// each call.
const wallClockFormatters = new Map();
const wallClockFormatter = (zone) => {
  if (wallClockFormatters.has(zone)) return wallClockFormatters.get(zone);
  let formatter = null;
  try {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    formatter = null;
  }
  wallClockFormatters.set(zone, formatter);
  return formatter;
};

export function instantToWallClock(instant, timeZone) {
  const ms =
    instant instanceof Date
      ? instant.getTime()
      : typeof instant === 'number'
        ? instant
        : Date.parse(String(instant ?? ''));
  if (!Number.isFinite(ms)) return null;

  const zone = String(timeZone ?? '').trim();
  const d = new Date(ms);
  const localFallback = () => ({
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  });
  if (!zone) return localFallback();

  try {
    const formatter = wallClockFormatter(zone);
    if (!formatter) return localFallback();
    const parts = Object.fromEntries(formatter.formatToParts(d).map((part) => [part.type, part.value]));
    return {
      date: `${parts.year}-${parts.month}-${parts.day}`,
      time: `${pad(Number(parts.hour) % 24)}:${parts.minute}`,
    };
  } catch {
    return localFallback();
  }
}

/**
 * Resolve the IANA timezone of a coordinate pair ({ lat, lng }). Offline
 * lookup — no network call — so it's safe to derive on every render. Returns
 * null when coords are missing/invalid, letting callers fall back to the
 * device timezone.
 */
export function coordsToTimeZone(coords) {
  // A null or blank coordinate is a missing one: Number(null) is 0.
  if (coords?.lat == null || coords?.lng == null || coords.lat === '' || coords.lng === '') return null;
  const lat = Number(coords.lat);
  const lng = Number(coords.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  // So is 0,0: no store or address is in the Gulf of Guinea, and an unset
  // pair stored as zeros read as Etc/GMT. On a restaurant that outranked the
  // real coordinates of the order's address (resolveSchedulingTimeZone).
  if (lat === 0 && lng === 0) return null;
  try {
    return tzLookup(lat, lng) || null;
  } catch {
    return null;
  }
}

/**
 * Re-express a wall clock from one timezone in another, as { date, time }.
 *
 * The fulfillment bar generates slots in the ADDRESS's timezone, but opening
 * hours and availability windows are wall-clock in the RESTAURANT's — so an
 * order delivered across a timezone boundary would otherwise be checked against
 * the wrong clock. Converting here keeps every hours comparison a plain string
 * compare downstream.
 *
 * Falls back to the input unchanged whenever either zone is missing or the
 * date/time is unparseable, so same-zone (the overwhelming majority) and
 * unknown-zone cases behave exactly as before. Conversion is a no-op when the
 * two zones are the same, so no equality check is needed.
 */
export function convertWallClock(dateValue, timeValue, fromTimeZone, toTimeZone) {
  const passthrough = { date: dateValue ?? null, time: timeValue ?? null };
  const from = String(fromTimeZone ?? '').trim();
  const to = String(toTimeZone ?? '').trim();
  if (!from || !to || from === to) return passthrough;

  const iso = wallClockToIso(dateValue, timeValue, from);
  if (!iso) return passthrough;
  return instantToWallClock(iso, to) || passthrough;
}

/**
 * The IANA timezone an order's wall clock is expressed in: the RESTAURANT's.
 *
 * A scheduled time means the restaurant's local clock for both pickup and
 * delivery — "12:00" is noon at the store, whoever placed it and wherever it
 * is dropped off. Resolving anything else lets the coach's device clock reach
 * the POS ticket.
 *
 * `restaurants.timezone` is nullable (it was introduced for Square locations),
 * so coordinates are the backstop: phantom sends latitude/longitude on every
 * restaurant and `coordsToTimeZone` is an offline lookup. Returns null only
 * when neither is known, which is the one case callers must treat as unknown
 * rather than silently device-local.
 */
export function resolveRestaurantTimeZone(restaurant) {
  if (!restaurant || typeof restaurant !== 'object') return null;

  const explicit =
    String(restaurant.timezone ?? restaurant.time_zone ?? '').trim() || null;
  if (explicit) return explicit;

  const lat = restaurant.latitude ?? restaurant.lat ?? restaurant.coords?.lat;
  const lng = restaurant.longitude ?? restaurant.lng ?? restaurant.coords?.lng;
  return coordsToTimeZone({ lat, lng });
}

// An IANA zone this runtime can format in, or null. A bad id would otherwise
// make wallClockToIso fall back to the device clock without a word.
const usableTimeZone = (value) => {
  const zone = String(value ?? '').trim();
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
};

/**
 * The zone a cart's or order's wall clock is written in, and where it came
 * from — the one resolver checkout, the order intent and cart surfaces share.
 *
 * The clock is the RESTAURANT's (see resolveRestaurantTimeZone). Rungs, best
 * first; each is tried across every restaurant object given before the next:
 *   'restaurant'        the restaurant object's own timezone
 *   'restaurant_row'    the coach `restaurants` row's timezone, when the
 *                       caller loaded it (every DoorDash row has one)
 *   'restaurant_coords' the restaurant object's latitude/longitude
 *   'discovery_coords'  the discovery `_coords` a cart snapshot carries
 *   'address_coords'    the fulfillment (delivery/pickup) address — a store
 *                       and its drop-off almost always share a zone
 *
 * A coordinate rung is skipped when its pair is missing, not a number, or
 * 0,0 (see coordsToTimeZone), so a placeholder never outranks a real place.
 *
 * `restaurant` may be one object or several views of the same store
 * (checkout holds both the cart snapshot's and the menu page's). Returns null
 * only when nothing is known: a caller that then uses the device clock must
 * say so (see resolveOrderClock).
 */
export function resolveSchedulingTimeZone(restaurant, fulfillmentCoords = null, { rowTimeZone = null } = {}) {
  const restaurants = (Array.isArray(restaurant) ? restaurant : [restaurant]).filter(
    (entry) => entry && typeof entry === 'object',
  );
  const first = (source, read) => {
    for (const entry of restaurants) {
      const timeZone = read(entry);
      if (timeZone) return { timeZone, source };
    }
    return null;
  };
  const rowZone = usableTimeZone(rowTimeZone);
  const addressZone = coordsToTimeZone(fulfillmentCoords);
  return (
    first('restaurant', (entry) => usableTimeZone(entry.timezone ?? entry.time_zone)) ||
    (rowZone ? { timeZone: rowZone, source: 'restaurant_row' } : null) ||
    first('restaurant_coords', (entry) =>
      coordsToTimeZone({
        lat: entry.latitude ?? entry.lat ?? entry.coords?.lat,
        lng: entry.longitude ?? entry.lng ?? entry.coords?.lng,
      }),
    ) ||
    first('discovery_coords', (entry) => coordsToTimeZone(entry._coords)) ||
    (addressZone ? { timeZone: addressZone, source: 'address_coords' } : null)
  );
}

/**
 * resolveSchedulingTimeZone with the last resort spelled out: when nothing
 * about the store or the address is known, the device's zone, marked
 * `source: 'device'` so the page names the zone it used instead of passing a
 * guess off as the restaurant's clock. Always returns an object; `timeZone`
 * is null only when the runtime can't name its own zone.
 */
export function resolveOrderClock(restaurant, fulfillmentCoords = null, options = {}) {
  return (
    resolveSchedulingTimeZone(restaurant, fulfillmentCoords, options) || {
      timeZone: deviceTimeZone(),
      source: 'device',
    }
  );
}

/**
 * The zone a CART's stored fulfillment_date/fulfillment_time is written in:
 * resolveSchedulingTimeZone's zone, or null when nothing is known (callers
 * then fall back to device-local).
 */
export function resolveCartTimeZone(restaurant, fulfillmentCoords, options = {}) {
  return resolveSchedulingTimeZone(restaurant, fulfillmentCoords, options)?.timeZone ?? null;
}

/**
 * Format an instant (Date, ms, or ISO string) in an explicit IANA timezone.
 *
 * Every order timestamp is stored as a UTC instant, so rendering it with a
 * bare `toLocaleString()` shows the VIEWER's clock — a coach in New York sees
 * a California pickup an hour off, and two teammates see different times for
 * the same order. Passing the order's timezone makes the rendering absolute.
 *
 * Falls back to the device clock only when the zone is missing/unknown, which
 * matches the old behavior for the legacy rows that have no zone recorded.
 * Returns null for an unparseable instant so callers can render a placeholder.
 */
export function formatInstantInTimeZone(instant, timeZone, options = {}, locale = 'en-US') {
  const ms =
    instant instanceof Date
      ? instant.getTime()
      : typeof instant === 'number'
        ? instant
        : Date.parse(String(instant ?? ''));
  if (!Number.isFinite(ms)) return null;

  const zone = String(timeZone ?? '').trim();
  const date = new Date(ms);
  try {
    return new Intl.DateTimeFormat(locale, zone ? { ...options, timeZone: zone } : options).format(date);
  } catch {
    // Unknown zone identifier — render in the device clock rather than throw.
    return new Intl.DateTimeFormat(locale, options).format(date);
  }
}

export default wallClockToIso;

/**
 * Short label for an IANA timezone at a given instant, e.g. "PDT". Returns null
 * for a missing/unknown zone so callers can simply omit the hint.
 */
export function formatTimeZoneLabel(timeZone, atMs = Date.now()) {
  const zone = String(timeZone ?? '').trim();
  if (!zone) return null;
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(new Date(atMs))
      .find((entry) => entry.type === 'timeZoneName');
    return part?.value || null;
  } catch {
    return null;
  }
}

/** The device's own IANA timezone, or null when it can't be determined. */
export function deviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * The zone's short name ("PDT") when it isn't the device's, else null: a
 * restaurant's wall clock reads as the viewer's own unless it is named
 * ("12:30 PM" on an Eastern laptop is 12:30 PDT, three hours later). Every
 * surface that shows a restaurant's time follows this one rule.
 * Named at `at` (the instant shown, or the slot picked), not now: a Nov 2
 * order is PST even while today is PDT.
 */
export function foreignTimeZoneLabel(timeZone, at = Date.now()) {
  if (!timeZone || timeZone === deviceTimeZone()) return null;
  const ms = at instanceof Date ? at.getTime() : typeof at === 'number' ? at : Date.parse(String(at ?? ''));
  return formatTimeZoneLabel(timeZone, Number.isFinite(ms) ? ms : Date.now());
}
